/**
 * Custom hook for AI Support Chat messages
 * 
 * Manages conversation thread and messages from supportConversations collection
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import firebase from 'firebase/compat/app';
import { consola } from 'consola';

interface AISupportSource {
  id: string;
  title?: string;
  slug?: string | null;
  category?: string | null;
  app_route?: string | null;
}

interface AISupportMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: (string | AISupportSource)[];
}

interface UseAISupportMessagesProps {
  userId: string;
  threadId?: string | null;
}

interface UseAISupportMessagesReturn {
  messages: AISupportMessage[];
  loading: boolean;
  threadId: string | null;
  sendMessage: (text: string) => Promise<void>;
  error: string | null;
}

export function useAISupportMessages({
  userId,
  threadId: initialThreadId,
}: UseAISupportMessagesProps): UseAISupportMessagesReturn {
  const [messages, setMessages] = useState<AISupportMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(initialThreadId || null);
  const [error, setError] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Load or create thread
  useEffect(() => {
    const initializeThread = async () => {
      if (!userId) return;

      try {
        const db = firebase.firestore();

        // If threadId is provided, use it
        if (threadId) {
          const threadDoc = await db.collection('supportConversations').doc(threadId).get();
          if (threadDoc.exists) {
            const threadData = threadDoc.data();
            const threadMessages = (threadData?.messages || []).map((msg: any, index: number) => ({
              id: `msg-${index}-${msg.timestamp?.seconds || Date.now()}`,
              role: msg.role,
              content: msg.content,
              timestamp: msg.timestamp?.toDate() || new Date(),
              sources: msg.sources,
            }));
            setMessages(threadMessages);
            return;
          }
        }

        // Try to find existing thread for this user
        // Query by authId first (matches Firestore security rules: authId == request.auth.uid)
        // Fall back to userId query for backwards compatibility with old threads
        let threadsQuery: firebase.firestore.QuerySnapshot | null = null;
        let foundThread = false;

        try {
          // Try authId query first (preferred - matches security rules)
          threadsQuery = await db
            .collection('supportConversations')
            .where('authId', '==', userId)
            .orderBy('updatedAt', 'desc')
            .limit(1)
            .get();
          foundThread = !threadsQuery.empty;
        } catch (authIdError: any) {
          // If authId index doesn't exist, query fails, or permission denied, try userId query
          const isIndexError = authIdError?.code === 'failed-precondition' || 
                              authIdError?.message?.includes('index');
          const isPermissionError = authIdError?.code === 'permission-denied';
          
          if (isIndexError) {
            consola.info('authId index not available, trying userId query');
          } else if (isPermissionError) {
            consola.info('authId query permission denied, trying userId query');
          } else {
            consola.warn('authId query failed, trying userId query:', authIdError);
          }
          
          // Fall back to userId query for backwards compatibility with old threads
          try {
            threadsQuery = await db
              .collection('supportConversations')
              .where('userId', '==', userId)
              .orderBy('updatedAt', 'desc')
              .limit(1)
              .get();
            foundThread = !threadsQuery.empty;
          } catch (fallbackError: any) {
            // If both queries fail, just continue without existing thread
            // User can still send messages to create a new thread
            const isFallbackIndexError = fallbackError?.code === 'failed-precondition' ||
                                        fallbackError?.message?.includes('index');
            const isFallbackPermissionError = fallbackError?.code === 'permission-denied';
            
            if (isFallbackIndexError) {
              consola.info('userId index also not available, starting fresh');
            } else if (isFallbackPermissionError) {
              consola.info('userId query also permission denied, starting fresh (will create new thread on first message)');
            } else {
              consola.warn('Both queries failed, starting fresh:', fallbackError);
            }
            threadsQuery = null;
            foundThread = false;
          }
        }

        if (foundThread && threadsQuery && !threadsQuery.empty) {
          const existingThread = threadsQuery.docs[0];
          setThreadId(existingThread.id);
          const threadData = existingThread.data();
          const threadMessages = (threadData?.messages || []).map((msg: any, index: number) => ({
            id: `msg-${index}-${msg.timestamp?.seconds || Date.now()}`,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp?.toDate() || new Date(),
            sources: msg.sources,
          }));
          setMessages(threadMessages);
        }
        // If no existing thread found, that's OK - user can send a message to create one
      } catch (err: any) {
        // Only log unexpected errors that aren't already handled above
        // This catch is for errors outside the query logic (e.g. Firestore connection issues)
        const isExpectedError = err?.code === 'failed-precondition' || 
                               err?.message?.includes('index') ||
                               err?.code === 'permission-denied';
        
        if (!isExpectedError) {
          consola.error('Unexpected error initializing AI thread:', err);
          setError('Failed to load conversation');
        } else {
          consola.info('Expected error during thread initialization (will create new thread on first message):', err.code);
        }
        // Don't set error state for expected errors - allow user to still use the chat (will create new thread on first message)
      }
    };

    initializeThread();
  }, [userId, threadId]);

  // Subscribe to thread updates
  useEffect(() => {
    if (!threadId) return;

    const db = firebase.firestore();
    const threadRef = db.collection('supportConversations').doc(threadId);

    const unsubscribe = threadRef.onSnapshot(
      (snapshot) => {
        if (snapshot.exists) {
          const threadData = snapshot.data();
          const threadMessages = (threadData?.messages || []).map((msg: any, index: number) => ({
            id: `msg-${index}-${msg.timestamp?.seconds || Date.now()}`,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp?.toDate() || new Date(),
            sources: msg.sources,
          }));
          setMessages(threadMessages);
        }
      },
      (err) => {
        consola.error('Error subscribing to thread:', err);
        setError('Failed to sync conversation');
      }
    );

    unsubscribeRef.current = unsubscribe;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [threadId]);

  // Send message to AI
  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || loading) return;

      setLoading(true);
      setError(null);

      try {
        const authToken = await firebase.auth().currentUser?.getIdToken();
        if (!authToken) {
          throw new Error('Not authenticated');
        }

        const API_BASE_URL = 'https://app.mighty45.com/api/v1';
      const response = await fetch(`${API_BASE_URL}/ai-support-chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            message: text,
            threadId: threadId || undefined,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          // Update thread ID if this was a new thread
          if (!threadId && data.threadId) {
            setThreadId(data.threadId);
          }
          // Messages will be updated via Firestore subscription
        } else {
          throw new Error(data.error || 'Failed to get response from AI');
        }
      } catch (err) {
        consola.error('Failed to send message to AI:', err);
        setError(err instanceof Error ? err.message : 'Failed to send message');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [threadId, loading]
  );

  return {
    messages,
    loading,
    threadId,
    sendMessage,
    error,
  };
}
